/**
 * Per-step action schema versions and migrations.
 * Action titles/buttons stay in step pages; this file tracks version numbers and id remaps.
 */

export interface StepActionMigration {
  /** Remap saved completion from old id -> new id */
  idRemap?: Record<number, number>;
  /** When an old id is completed, also mark these new ids complete (after idRemap) */
  alsoCompleteOnRemap?: Record<number, number[]>;
  /** New action ids introduced in this version (for documentation / tests) */
  newActionIds?: number[];
}

export interface StepActionSchema {
  stepNumber: number;
  actionSchemaVersion: number;
  migrations: Record<number, StepActionMigration>;
}

const legacyMigration: StepActionMigration = {};

function schema(stepNumber: number, actionSchemaVersion: number): StepActionSchema {
  return {
    stepNumber,
    actionSchemaVersion,
    migrations: {
      1: legacyMigration, // version 0 (missing) -> 1
    },
  };
}

function schemaV2(
  stepNumber: number,
  migration: StepActionMigration
): StepActionSchema {
  return {
    stepNumber,
    actionSchemaVersion: 2,
    migrations: {
      1: legacyMigration,
      2: migration,
    },
  };
}

/** Current schema version per step. */
export const STEP_ACTION_SCHEMAS: Record<number, StepActionSchema> = {
  0: schema(0, 1),
  1: schema(1, 1),
  2: schemaV2(2, { idRemap: { 4: 5 }, newActionIds: [4] }),
  3: {
    stepNumber: 3,
    actionSchemaVersion: 3,
    migrations: {
      1: legacyMigration,
      2: { idRemap: { 4: 5 }, newActionIds: [4] },
      3: {
        idRemap: { 4: 5, 5: 6 },
        alsoCompleteOnRemap: { 5: [4] },
        newActionIds: [6],
      },
    },
  },
  4: schemaV2(4, { idRemap: { 4: 5 }, newActionIds: [4] }),
  5: {
    stepNumber: 5,
    actionSchemaVersion: 2,
    migrations: {
      1: legacyMigration,
      2: {
        alsoCompleteOnRemap: { 3: [4] },
        newActionIds: [4, 5, 6],
      },
    },
  },
  6: schemaV2(6, { idRemap: { 3: 5 }, newActionIds: [4] }),
  7: {
    stepNumber: 7,
    actionSchemaVersion: 3,
    migrations: {
      1: legacyMigration,
      2: { idRemap: { 3: 4 }, newActionIds: [3] },
      3: {
        idRemap: { 3: 4, 4: 5 },
        alsoCompleteOnRemap: { 4: [3] },
        newActionIds: [5],
      },
    },
  },
  8: schemaV2(8, { newActionIds: [5] }),
  9: schemaV2(9, { idRemap: { 4: 5 }, newActionIds: [4] }),
  10: schemaV2(10, { idRemap: { 4: 5 }, newActionIds: [4] }),
  11: schemaV2(11, { idRemap: { 3: 5 }, alsoCompleteOnRemap: { 3: [3, 4] }, newActionIds: [4, 5] }),
  12: schemaV2(12, { idRemap: { 3: 5 }, alsoCompleteOnRemap: { 3: [3, 4] }, newActionIds: [4, 5] }),
};

export function getStepActionSchema(stepNumber: number): StepActionSchema {
  const s = STEP_ACTION_SCHEMAS[stepNumber];
  if (!s) {
    throw new Error(`No action schema for step ${stepNumber}`);
  }
  return s;
}
